/**
 * identification — presentation logic for ADR 0007's identification slot
 * duplication (#275).
 *
 * Under the {CURP}_{TIPO} intake a single identification file fills BOTH INE
 * slots (`ine` + `ine_reverso`) with the same bytes, so the detail would show
 * the identical image twice — which reads as a bug to a reviewer who does not
 * know the rule. The API marks each document with an opaque `content_digest`
 * (equal values ⇔ identical stored bytes); when both slots provably share one
 * source document, the console says so in domain terms.
 *
 * Presentation only: verdicts, checks and the audit record are untouched.
 */

import type { DocumentRef } from "../../api/client";

/** True iff both INE slots exist and PROVABLY hold the same stored bytes.
 * A digest absent on either side proves nothing — never signal on absence. */
export function isSingleIdentificationSource(documents: DocumentRef[] | null): boolean {
  if (!documents) return false;
  const front = documents.find((d) => d.tipo === "ine");
  const back = documents.find((d) => d.tipo === "ine_reverso");
  return Boolean(
    front?.content_digest && back?.content_digest &&
    front.content_digest === back.content_digest,
  );
}

/** The workbench notice for the single-source case, in domain terms. */
export const SINGLE_IDENTIFICATION_NOTICE =
  "Documento de identificación único: el aspirante entregó un solo archivo y " +
  "Vera lo evaluó por ambos lados. INE (frente) e INE (reverso) muestran el " +
  "mismo documento.";

/** Reviewer-facing motivo for the IDEN_VALIDATION_PENDING flag (né INE_VALIDATION_PENDING).
 *
 * With two distinct identification documents the engine motivo passes through
 * untouched (that path renders exactly as before #275). In the single-source
 * case the engine wording ("Reverso del INE no disponible…") contradicts what
 * the reviewer sees — a reverso card IS on screen (the same single file) — so
 * the domain framing is PREPENDED and the engine motivo kept verbatim: no
 * information is ever dropped (a mixed motivo like "INE no legible (vigencia,
 * MRZ)…" keeps its front-field part), and nothing dispatches on the engine's
 * prose, which is free to change.
 */
export function ineValidationPendingDetail(
  detail: string | null | undefined,
  singleSource: boolean,
): string | null {
  if (!singleSource) return detail ?? null;
  const context =
    "El aspirante entregó un solo documento de identificación y Vera lo " +
    "evaluó por ambos lados";
  return detail
    ? `${context} — ${detail}`
    : `${context}; la validación interna del INE quedó pendiente de revisión humana.`;
}
